'use client';

import { AuthContextProvider } from '@/contexts/AuthContext/AuthContext';
import EventEmitterProvider from '@/contexts/EventEmitterContext/EventEmitterProvider';
import { MediaQueryContextProvider } from '@/contexts/MediaQueryContext';
import SessionExpiredDialog from '@/features/auth/components/SessionExpiredDialog';
import { SESSION_REFETCH_INTERVAL_SECONDS } from '@/features/auth/constants';
import { ApiError, HttpStatusCode } from '@/services/ApiError';
import { MetrisUIProvider } from '@andritzot/metris-web-ui/context';
import { useTheme } from '@andritzot/metris-web-utils/store/useTheme';
import PiwikProProvider from '@piwikpro/next-piwik-pro';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from 'next-auth/react';
import dynamic from 'next/dynamic';
import { PrimeReactProvider } from 'primereact/api';
import { useEffect, useState } from 'react';

const isDev = process.env.NODE_ENV === 'development';

const ReactQueryDevtools = isDev
  ? dynamic(() => import('@tanstack/react-query-devtools').then((mod) => mod.ReactQueryDevtools), { ssr: false })
  : () => null;

const ClickToComponent = isDev
  ? dynamic(
      () =>
        import('click-to-react-component').then(
          (mod: { ClickToComponent: React.ComponentType }) => mod.ClickToComponent,
        ),
      { ssr: false },
    )
  : () => null;

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        // A 401 has already been through the API client's own refresh-and-replay, so retrying it here
        // only multiplies requests against a session that cannot be repaired without the user.
        retry: (failureCount, error) =>
          failureCount < 3 && !(error instanceof ApiError && error.statusCode === HttpStatusCode.Unauthorized),
      },
    },
  });
}

export const Providers = ({ children }: { children: React.ReactNode }) => {
  const [queryClient] = useState(makeQueryClient);
  const { switchTheme } = useTheme();

  useEffect(() => {
    switchTheme('light');
  }, [switchTheme]);

  return (
    <PrimeReactProvider value={{ zIndex: { modal: 9100, overlay: 9200, menu: 9300, tooltip: 9400 } }}>
      <EventEmitterProvider>
        {/* Polling keeps an idle tab inside Keycloak's SSO idle timeout. refetchOnWindowFocus is off
            because this next-auth version never populates its throttle window, so every return to the
            tab costs a session read and a tree re-render; a returning user is covered by the poll and
            by the API client's on-demand session read. */}
        <SessionProvider refetchOnWindowFocus={false} refetchInterval={SESSION_REFETCH_INTERVAL_SECONDS}>
          <QueryClientProvider client={queryClient}>
            <MetrisUIProvider>
              <AuthContextProvider>
                <MediaQueryContextProvider>
                  {isDev ? (
                    children
                  ) : (
                    <PiwikProProvider
                      containerUrl={process.env.NEXT_PUBLIC_CONTAINER_URL ?? ''}
                      containerId={process.env.NEXT_PUBLIC_CONTAINER_ID ?? ''}
                    >
                      {children}
                    </PiwikProProvider>
                  )}
                </MediaQueryContextProvider>
                <SessionExpiredDialog />
                <ReactQueryDevtools initialIsOpen={false} />
                <ClickToComponent />
              </AuthContextProvider>
            </MetrisUIProvider>
          </QueryClientProvider>
        </SessionProvider>
      </EventEmitterProvider>
    </PrimeReactProvider>
  );
};
